import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from 'react';
import { gekkoApi } from '../../api/GekkoApi';
import { PaginatedResponse } from '../../types/api.types';

// Breed interface
export interface Breed {
  id: number;
  name: string;
  animal_type_id: number;
}

// Context interface
export interface BreedsContextType {
  breeds: Record<number, Breed[]>;
  isLoading: boolean;
  error: string | null;
  fetchBreedsByAnimalTypeId: (animalTypeId: number) => Promise<Breed[]>;
}

// Create the context with a default value
const BreedsContext = createContext<BreedsContextType | undefined>(undefined);

// Create the provider component
interface BreedsProviderProps {
  children: ReactNode;
}

export const BreedsProvider: React.FC<BreedsProviderProps> = ({ children }) => {
  const [breeds, setBreeds] = useState<Record<number, Breed[]>>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Function to fetch breeds by animal type ID from the API
  const fetchBreedsByAnimalTypeId = useCallback(
    async (animalTypeId: number) => {
      // Return cached breeds if already fetched
      if (breeds[animalTypeId]) {
        return breeds[animalTypeId];
      }

      setIsLoading(true);
      setError(null);

      try {
        let allBreeds: Breed[] = [];
        let currentPage = 1;
        let hasMorePages = true;
        const pageSize = 100;

        // Fetch all pages until there are no more items
        while (hasMorePages) {
          const response = await gekkoApi.get<PaginatedResponse<Breed>>(
            '/breeds',
            {
              queryParams: {
                animal_type_id: animalTypeId,
                page_size: pageSize,
                page: currentPage,
              },
            }
          );

          if (response.items && response.items.length > 0) {
            allBreeds = [...allBreeds, ...response.items];

            // Check if we've received fewer items than the page size or exactly the page size but total items reached
            hasMorePages =
              response.items.length === pageSize &&
              (response.total_items
                ? allBreeds.length < response.total_items
                : true);

            currentPage++;
          } else {
            // No more items
            hasMorePages = false;
          }
        }

        // Update state with all fetched breeds
        setBreeds((prevBreeds) => ({
          ...prevBreeds,
          [animalTypeId]: allBreeds,
        }));

        return allBreeds;
      } catch (err) {
        console.error(
          `Error fetching breeds for animal type ID ${animalTypeId}:`,
          err
        );
        setError(
          `Failed to fetch breeds for the selected animal type. Please try again later.`
        );
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [breeds]
  );

  // Context value
  const value: BreedsContextType = {
    breeds,
    isLoading,
    error,
    fetchBreedsByAnimalTypeId,
  };

  return (
    <BreedsContext.Provider value={value}>{children}</BreedsContext.Provider>
  );
};

// Custom hook to use the breeds context
export const useBreeds = () => {
  const context = useContext(BreedsContext);
  if (context === undefined) {
    throw new Error('useBreeds must be used within a BreedsProvider');
  }
  return context;
};
